<template>
  <v-card elevation="0">
    <warehouse-entry-grocery-dialog
      v-model="entryDialog"
      :warehouse="editedItem?.warehouse || {}"
      :editedItem="editedItem"
      v-if="editedItem"
      @success="onEntrySuccess"
    >
    </warehouse-entry-grocery-dialog>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <!-- Show grouped by warehouse if multiple warehouses selected -->
        <template v-if="filters.warehouse.length > 1">
          <mobile-data-cards
            v-for="(group, warehouseId) in documentsByWarehouse"
            :key="warehouseId"
            :headers="mobileHeaders"
            :items="group.items"
            :loading="loading"
            :title="group.warehouseName"
            item-key="id"
            :fields-per-row="5"
            :show-row-number="false"
            :bold-fields="['number']"
            class="mobile-table-compact"
          >
            <template v-slot:item.document_date="{ item }">
              {{
                localeDate(new Date(item.document_date), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              }}
            </template>
            <template v-slot:item.total_items="{ item }">
              {{ item.groceries ? item.groceries.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-spacer></v-spacer>
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
              ></circle-button>
            </template>
          </mobile-data-cards>
        </template>
        <!-- Show single list if only one warehouse selected -->
        <template v-else>
          <mobile-data-cards
            :headers="mobileHeaders"
            :items="filteredDocuments"
            :loading="loading"
            item-key="id"
            :fields-per-row="5"
            :show-row-number="false"
            :bold-fields="['number']"
            class="mobile-table-compact"
          >
            <template v-slot:item.document_date="{ item }">
              {{
                localeDate(new Date(item.document_date), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              }}
            </template>
            <template v-slot:item.total_items="{ item }">
              {{ item.groceries ? item.groceries.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-spacer></v-spacer>
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
              ></circle-button>
            </template>
          </mobile-data-cards>
        </template>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-package-variant-plus"
        :title="$t('warehouse.no_entries')"
        :subtitle="$t('warehouse.no_entries_yet')"
      >
        <v-btn color="primary" @click="openAddDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("add_warehouse") }}
        </v-btn>
      </empty-state>

      <!-- Floating Action Button for Mobile -->
      <v-btn
        v-if="filteredDocuments.length > 0"
        color="primary"
        fab
        fixed
        bottom
        right
        @click="openAddDialog"
        class="mb-12"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        :loading="loading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer></v-spacer>
            <warehouse-button-import-articles
              @click="openAddDialog"
            ></warehouse-button-import-articles>
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.number="{ item }">
          {{ item.number || "-" }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{
            localeDate(new Date(item.document_date), {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.total_items="{ item }">
          {{ item.groceries ? item.groceries.length : 0 }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            @click="openDocument(item)"
            icon="mdi-eye"
            :tooltip="$t('overview')"
            color="primary"
          ></circle-button>
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-package-variant-plus"
        :title="$t('warehouse.no_entries')"
        :subtitle="$t('warehouse.no_entries_yet')"
      >
        <warehouse-button-import-articles
          @click="openAddDialog"
        ></warehouse-button-import-articles>
      </empty-state>
    </template>
  </v-card>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import WarehouseEntryGroceryDialog from "@/components/warehouse/entry/WarehouseEntryGroceryDialog.vue";
import { WarehouseButtonImportArticles } from "@/components/warehouse/buttons";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import { documentGrocery } from "@/models/warehouse";

export default {
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  components: {
    WarehouseEntryGroceryDialog,
    WarehouseButtonImportArticles,
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      documents: [],
      loading: false,
      entryDialog: false,
      editedItem: {},
    };
  },
  methods: {
    onEntrySuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await warehouseService.getWarehouseDocuments({
          document_type: "entry",
        });
        this.documents = data;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_documents"));
      } finally {
        this.loading = false;
      }
    },
    openDocument(item) {
      this.editedItem = { ...item };
      this.entryDialog = true;
    },
    openAddDialog() {
      this.editedItem = {
        ...documentGrocery,
        warehouse: this.filters.warehouse[0] || {},
      };
      this.entryDialog = true;
    },
  },
  computed: {
    filters() {
      return this.$store.state.warehouse.filters;
    },
    filteredDocuments() {
      const { from, to } = this.filters.dates;
      if (!from || !to) {
        return this.documents.filter((item) =>
          this.filters.warehouse.some((warehouse) => warehouse.id === item.warehouse_id)
        );
      }

      const fromDate = new Date(from + "T00:00:00");
      const toDate = new Date(to + "T23:59:59");

      return this.documents.filter((item) => {
        const documentDate = new Date(item.document_date);
        const dateFilter = documentDate >= fromDate && documentDate <= toDate;
        const warehouseFilter = this.filters.warehouse.some(
          (warehouse) => warehouse.id === item.warehouse_id
        );
        return dateFilter && warehouseFilter;
      });
    },
    headers() {
      const baseHeaders = [];

      baseHeaders.push(
        {
          text: "ID",
          value: "id",
          cols: 3,
          mobile: false
        },
        {
          text: this.$t("stock"),
          align: "start",
          value: "warehouse.name",
          cols: 4,
          mobile: false,
        },
        {
          text: this.$t("warehouse.document_number"),
          value: "number",
          cols: 3,
        },
        {
          text: this.$t("date"),
          value: "document_date",
          cols: 4,
          print: false,
        },
        {
          text: this.$t("warehouse.items_count"),
          value: "total_items",
          cols: 3,
          align: "center",
        },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          print: false,
          cols: 2,
          width: "100px",
        }
      );

      return baseHeaders;
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("warehouse.unknown"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    exportItems() {
      return this.filteredDocuments.map((item) => {
        return {
          ...item,
          total_items: item.groceries ? item.groceries.length : 0,
        };
      });
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.warehouse_replenishment"),
      ].join(" ");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
      immediate: true,
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.exportItems,
            },
          ],
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.exportItems,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
